import type { ComponentProps, ReactNode } from 'react';
import { Button, ProgressRail } from '../ui/kit';

/**
 * The journey step's action row — the step-forward CTA.
 *
 * DESKTOP (>=880px): an ordinary inline row in normal document flow, exactly as
 * before. MOBILE (<880px, CTO 2026-09-24): `.gps-jux-action-row` makes it
 * `position: sticky; bottom: 0` over a near-opaque white surface with a
 * hairline, so the button stays under the patient's thumb while a long screen
 * scrolls beneath it, and settles back into its slot at the end of the screen
 * (see the rule in tokens.css, L1). The class is the whole mechanism — the
 * inline style carries no position, so this row is in flow wherever the
 * stylesheet does not apply (and in jsdom).
 *
 * ⚠️ Buttons only. On a phone the row is a stacking context (sticky +
 * z-index), so a DialogShell rendered INSIDE it would be trapped under the top
 * chrome. Dialogs belong beside the row, never in it.
 */
export function ActionRow({ children }: { children: ReactNode }) {
  return (
    <div className="gps-jux-action-row" style={{ marginTop: 'var(--gps-s-5)' }}>
      {children}
    </div>
  );
}

/**
 * Journey-local back control: same 44px circular hit area and "Back" label as
 * the shared kit BackButton, with the journey polish (SVG chevron + the
 * desktop-only hover slide via `.gps-back-arrow` in tokens.css).
 */
export function JourneyBackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className="gps-back-btn gps-journey-back-btn"
      onClick={onClick}
      aria-label="Back"
      style={{
        width: '44px',
        height: '44px',
        borderRadius: 'var(--gps-r-pill)',
        border: '1px solid var(--gps-border-strong)',
        background: 'var(--gps-surface)',
        color: 'var(--gps-text)',
        display: 'grid',
        placeItems: 'center',
        cursor: 'pointer',
        flex: 'none',
      }}
    >
      <svg aria-hidden="true" viewBox="0 0 24 24" width={16} height={16} className="gps-back-arrow">
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M14.5 5l-7 7 7 7"
        />
      </svg>
    </button>
  );
}

/**
 * Journey-local flow app-bar (back + progress + step count). Mirrors the
 * shared kit AppBar layout but owns the journey rhythm: no bottom margin here
 * — JourneyScreen owns the gap below — and the journey back button with its
 * chevron polish.
 */
export function JourneyAppBar({
  onBack,
  percent,
  step,
  progressLabel,
}: {
  onBack?: () => void;
  percent?: number;
  step?: string;
  progressLabel?: string;
}) {
  return (
    // `gps-jux-appbar` gives the bar its own paint layer (tokens.css, P8 "THE
    // BLOOM"): the Result screen's glow reaches up behind it, and without the
    // layer the glow was painted OVER the progress line, washing it out.
    <div
      className="gps-jux-appbar"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--gps-s-3)',
        marginBottom: 0,
        minHeight: '44px',
      }}
    >
      {onBack ? <JourneyBackButton onClick={onBack} /> : null}
      {typeof percent === 'number' ? <ProgressRail percent={percent} label={progressLabel} /> : null}
      {step ? (
        <span
          style={{
            fontSize: '12px',
            color: 'var(--gps-muted)',
            fontVariantNumeric: 'tabular-nums',
            flex: 'none',
          }}
        >
          {step}
        </span>
      ) : null}
    </div>
  );
}

/** Journey-only button decoration, kept out of the shared Button API. */
export function JourneyButton({
  children,
  arrow = false,
  outline = false,
  className,
  style,
  ...props
}: ComponentProps<typeof Button> & { arrow?: boolean; outline?: boolean }) {
  return (
    <Button
      {...props}
      variant={outline ? 'ghost' : props.variant}
      className={[outline ? 'gps-journey-button-outline' : null, className].filter(Boolean).join(' ')}
      style={{
        ...(outline
          ? {
              borderColor: 'var(--gps-primary)',
              color: 'var(--gps-green-text)',
            }
          : {}),
        ...style,
      }}
    >
      {children}
      {arrow ? <ButtonArrow /> : null}
    </Button>
  );
}

/** Forward-arrow icon for journey CTAs. */
export function ButtonArrow() {
  return (
    <svg
      aria-hidden="true"
      role="img"
      viewBox="0 0 20 21"
      width={16}
      height={16}
      xmlns="http://www.w3.org/2000/svg"
      className="gps-btn-arrow"
      style={{ flexShrink: 0 }}
    >
      <polygon
        fill="currentColor"
        points="16.172 9 10.101 2.929 11.515 1.515 20 10 19.293 10.707 11.515 18.485 10.101 17.071 16.172 11 0 11 0 9"
      />
    </svg>
  );
}
